<template>
  <div class="chat-questionnaire-section">
    <div class="chat-questionnaire">
      <!-- メッセージリスト（スクロール範囲） -->
      <div ref="messageListRef" class="chat-questionnaire__message-list">
        <!-- イントロセクション -->
        <IntroSection
          :total-count="totalCount"
          :doctor-icon-url="doctorIconUrl"
        />

        <!-- 最初の挨拶メッセージ -->
        <div class="chat-questionnaire__message-item">
          <ChatMessage :doctor-icon-url="doctorIconUrl">
            こんにちは。{{ keyword }}についてお悩みですね。
          </ChatMessage>
        </div>

        <!-- Step 1: カテゴリ選択の質問 -->
        <div
          v-if="showStep('category')"
          class="chat-questionnaire__message-item"
        >
          <ChatMessage :doctor-icon-url="doctorIconUrl">
            知りたいことをお選びください。
          </ChatMessage>
        </div>

        <!-- Step 1: カテゴリ選択の回答 -->
        <div
          v-if="isStepAnswered('category')"
          class="chat-questionnaire__message-item"
        >
          <UserAnswer>
            {{ getCategoryLabel(formData.category) }}
          </UserAnswer>
        </div>

        <!-- Step 2: 期間選択の質問 -->
        <div
          v-if="showStep('duration')"
          class="chat-questionnaire__message-item"
        >
          <ChatMessage :doctor-icon-url="doctorIconUrl">
            ご回答ありがとうございます。<br />症状はいつ頃から始まりましたか？
          </ChatMessage>
        </div>

        <!-- Step 2: 期間選択の回答 -->
        <div
          v-if="isStepAnswered('duration')"
          class="chat-questionnaire__message-item"
        >
          <UserAnswer>
            {{ getDurationLabel(formData.duration) }}
          </UserAnswer>
        </div>

        <!-- Step 3: 受診状況選択の質問 -->
        <div
          v-if="showStep('medicalVisit')"
          class="chat-questionnaire__message-item"
        >
          <ChatMessage :doctor-icon-url="doctorIconUrl">
            ありがとうございます。こちらが最後の質問です。<br />{{
              keyword
            }}に関して医療機関を受診していますか？
          </ChatMessage>
        </div>

        <!-- Step 3: 受診状況選択の回答 -->
        <div
          v-if="isStepAnswered('medicalVisit')"
          class="chat-questionnaire__message-item"
        >
          <UserAnswer>
            {{ getMedicalVisitLabel(formData.medicalVisit) }}
          </UserAnswer>
        </div>

        <!-- Step 4: 自由入力の質問 -->
        <div
          v-if="showStep('freeText')"
          class="chat-questionnaire__message-item"
        >
          <ChatMessage :doctor-icon-url="doctorIconUrl">
            ご回答ありがとうございました。<br />
            下の相談を見つけるボタンを押してください。<br />
            もし、他にも気になることがありましたら、文章でもご記入いただけます。（任意）
          </ChatMessage>
        </div>
      </div>

      <!-- 入力エリア（固定、スクロール範囲外） -->
      <div v-if="isStep" class="chat-questionnaire__input-area">
        <!-- Step 1: カテゴリ選択の選択肢 -->
        <div
          v-if="isCurrentStep('category')"
          class="chat-questionnaire__options"
        >
          <button
            v-for="option in categoryOptionsWithSkip"
            :key="option.label"
            type="button"
            class="chat-questionnaire__option-btn"
            @click="handleCategorySelect(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <!-- Step 2: 期間選択の選択肢 -->
        <div
          v-if="isCurrentStep('duration')"
          class="chat-questionnaire__options"
        >
          <button
            v-for="option in durationOptionsWithSkip"
            :key="option.label"
            type="button"
            class="chat-questionnaire__option-btn"
            @click="handleDurationSelect(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <!-- Step 3: 受診状況選択の選択肢 -->
        <div
          v-if="isCurrentStep('medicalVisit')"
          class="chat-questionnaire__options"
        >
          <button
            v-for="option in medicalVisitOptionsWithSkip"
            :key="option.label"
            type="button"
            class="chat-questionnaire__option-btn"
            @click="handleMedicalVisitSelect(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <!-- Step 4: 自由入力 -->
        <div
          v-if="isCurrentStep('freeText')"
          class="chat-questionnaire__text-input"
        >
          <FreeTextInput
            v-model="formData.freeText"
            class="chat-questionnaire-free-text"
            placeholder="例）朝起きたときに症状が一番辛くなり、ズキズキと痛みます&#10;例）健康診断では肝臓の数値が良くないと言われています"
          />

          <!-- 年齢・性別選択（任意） -->
          <div class="chat-questionnaire__optional-fields">
            <!-- 年齢 -->
            <div class="chat-questionnaire__field-group">
              <label class="chat-questionnaire__field-label"
                >年齢（任意）</label
              >
              <div class="chat-questionnaire__field-options">
                <button
                  v-for="age in ageOptions"
                  :key="age.value"
                  type="button"
                  class="chat-questionnaire__field-option"
                  :class="{ selected: formData.targetAge === age.value }"
                  @click="
                    formData.targetAge =
                      formData.targetAge === age.value
                        ? 'NOT_SELECTED'
                        : age.value
                  "
                >
                  {{ age.label }}
                </button>
              </div>
            </div>

            <!-- 性別 -->
            <div class="chat-questionnaire__field-group">
              <label class="chat-questionnaire__field-label"
                >性別（任意）</label
              >
              <div class="chat-questionnaire__field-options">
                <button
                  v-for="gender in genderOptions"
                  :key="gender.value"
                  type="button"
                  class="chat-questionnaire__field-option"
                  :class="{ selected: formData.targetGender === gender.value }"
                  @click="
                    formData.targetGender =
                      formData.targetGender === gender.value
                        ? 'NOT_SELECTED'
                        : gender.value
                  "
                >
                  {{ gender.label }}
                </button>
              </div>
            </div>
          </div>

          <div class="chat-questionnaire__text-actions">
            <button
              type="button"
              class="chat-questionnaire__submit-btn"
              @click="handleSearch"
            >
              相談を見つける（無料）
            </button>
          </div>
        </div>
      </div>

      <!-- 検索結果 -->
      <div
        v-if="hasSearched"
        ref="resultsContainerRef"
        class="chat-questionnaire__results"
      >
        <!-- ローディング -->
        <LoadingBar v-if="isLoading" />
        <!-- 検索結果 -->
        <SearchResults
          v-else
          v-model="searchConditionData"
          :results="results"
          :has-error="hasError"
          :keyword="keyword"
          :is-loading="isLoading"
          :show-close-button="false"
          ga-category="問診LP_chat"
          @retry="handleRetry"
          @re-search="handleReSearch"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, nextTick } from 'vue';
import IntroSection from './IntroSection.vue';
import ChatMessage from './ChatMessage.vue';
import UserAnswer from './UserAnswer.vue';
import FreeTextInput from '../FreeTextInput.vue';
import LoadingBar from '../../../similar_search/components/LoadingBar.vue';
import SearchResults from '../SearchResults.vue';
import { searchSimilarTopics } from '../../../similar_search/api';
import { buildSearchQuery } from '../../buildSearchQuery';
import {
  categoryOptions,
  durationOptions,
  medicalVisitOptions,
  ageOptions,
  genderOptions,
} from '../../questions';
import type {
  InquiryStep,
  InquiryFormData,
  TargetAge,
  TargetGender,
  SimilarSearchResult,
  CategoryOption,
  DurationOption,
  MedicalVisitOption,
} from '../../types';

const props = defineProps<{
  keyword: string;
  targetAge: TargetAge;
  targetGender: TargetGender;
  totalCount: number;
  doctorIconUrl: string;
}>();

// DOM refs
const messageListRef = ref<HTMLElement | null>(null);
const resultsContainerRef = ref<HTMLElement | null>(null);

// ステップの順序定義
const stepOrder: InquiryStep[] = [
  'category',
  'duration',
  'medicalVisit',
  'freeText',
];

// ステップ管理（インデックスで管理）
const currentStepIndex = ref(0);

// 最後に回答したステップ（currentStepIndexは300ms待機後に変更されるが、
// こちらは回答した瞬間に変更することで、回答吹き出しを即座に表示してアニメーションのタイミングを分ける）
// -1 は「未回答」を意味する
const lastAnsweredStepIndex = ref(-1);

// 非同期状態管理
const isLoading = ref(false);
const hasSearched = ref(false);
const hasError = ref(false);

// フォームデータ
const formData = reactive<InquiryFormData>({
  keyword: props.keyword,
  targetAge: props.targetAge,
  targetGender: props.targetGender,
  category: null,
  duration: null,
  medicalVisit: null,
  freeText: '',
});

// 検索結果
const results = ref<SimilarSearchResult[]>([]);

// 派生状態
const isStep = computed(() => !hasSearched.value && !isLoading.value);

// 「回答しない」オプションを追加した選択肢
const categoryOptionsWithSkip = computed(() => [
  ...categoryOptions,
  { label: '回答しない', value: null },
]);

const durationOptionsWithSkip = computed(() => [
  ...durationOptions,
  { label: '回答しない', value: null },
]);

const medicalVisitOptionsWithSkip = computed(() => [
  ...medicalVisitOptions,
  { label: '回答しない', value: null },
]);

// 特定のステップを表示するかどうか（チャット形式：現在以前のステップは全て表示）
const showStep = (step: InquiryStep) => {
  const stepIndex = stepOrder.indexOf(step);
  return stepIndex <= currentStepIndex.value;
};

// 特定のステップが回答済みかどうか
const isStepAnswered = (step: InquiryStep) => {
  if (lastAnsweredStepIndex.value < 0) return false;
  const stepIndex = stepOrder.indexOf(step);
  return stepIndex <= lastAnsweredStepIndex.value;
};

// 現在のステップが指定されたステップかどうか
const isCurrentStep = (step: InquiryStep) => {
  return stepOrder[currentStepIndex.value] === step;
};

// ラベル取得関数
const getCategoryLabel = (value: CategoryOption | null) => {
  if (!value) return '回答しない';
  const option = categoryOptions.find((opt) => opt.value === value);
  return option ? option.label : '';
};

const getDurationLabel = (value: DurationOption | null) => {
  if (!value) return '回答しない';
  const option = durationOptions.find((opt) => opt.value === value);
  return option ? option.label : '';
};

const getMedicalVisitLabel = (value: MedicalVisitOption | null) => {
  if (!value) return '回答しない';
  const option = medicalVisitOptions.find((opt) => opt.value === value);
  return option ? option.label : '';
};

// スクロール機能
const scrollToBottom = () => {
  if (messageListRef.value) {
    messageListRef.value.scrollTo({
      top: messageListRef.value.scrollHeight,
      behavior: 'smooth',
    });
  }
};

const scrollToResults = () => {
  if (resultsContainerRef.value) {
    resultsContainerRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

// 選択ハンドラー
const handleCategorySelect = (value: CategoryOption | null) => {
  formData.category = value;
  handleStepNext();
};

const handleDurationSelect = (value: DurationOption | null) => {
  formData.duration = value;
  handleStepNext();
};

const handleMedicalVisitSelect = (value: MedicalVisitOption | null) => {
  formData.medicalVisit = value;
  handleStepNext();
};

// フォームデータを別のオブジェクトにコピーする
const copyFormData = ({
  target,
  source,
}: {
  target: InquiryFormData;
  source: InquiryFormData;
}): void => {
  target.keyword = source.keyword;
  target.targetAge = source.targetAge;
  target.targetGender = source.targetGender;
  target.category = source.category;
  target.duration = source.duration;
  target.medicalVisit = source.medicalVisit;
  target.freeText = source.freeText;
};

// 検索条件データ（SearchConditionAccordion専用）
const searchConditionData = ref<InquiryFormData>({
  keyword: props.keyword,
  targetAge: props.targetAge,
  targetGender: props.targetGender,
  category: null,
  duration: null,
  medicalVisit: null,
  freeText: '',
});

// 次のステップへ
const handleStepNext = async () => {
  const currentStepName = stepOrder[currentStepIndex.value];

  if (currentStepIndex.value < stepOrder.length - 1) {
    // 最後に回答したステップを記録
    lastAnsweredStepIndex.value = currentStepIndex.value;

    // GA送信
    let answerValue = '';
    if (currentStepName === 'category' && formData.category) {
      answerValue = formData.category;
    } else if (currentStepName === 'duration' && formData.duration) {
      answerValue = formData.duration;
    } else if (currentStepName === 'medicalVisit' && formData.medicalVisit) {
      answerValue = formData.medicalVisit;
    }

    window.sendGAEvent(
      '問診LP_chat',
      'answer',
      `step_${currentStepName}_${answerValue}`,
    );

    // ユーザーの回答を表示してスクロール
    await nextTick();
    scrollToBottom();

    // 300ms待ってから次の質問を表示
    await new Promise((resolve) => setTimeout(resolve, 300));
    currentStepIndex.value++;

    // 医師の質問を表示してスクロール
    await nextTick();
    scrollToBottom();
  }
};

// 検索実行
const handleSearch = async () => {
  window.sendGAEvent(
    '問診LP_chat',
    'search',
    isCurrentStep('freeText') && formData.freeText
      ? `freeText_length_${formData.freeText.length}`
      : 'from_steps',
  );

  // 常にformDataをsearchConditionDataにコピー
  copyFormData({ target: searchConditionData.value, source: formData });

  // ローディング開始
  hasSearched.value = true;
  hasError.value = false;
  isLoading.value = true;

  try {
    const query = buildSearchQuery(searchConditionData.value);
    const response = await searchSimilarTopics({ query });
    results.value = response.results;

    window.sendGAEvent('問診LP_chat', 'result', `${response.results.length}件`);
  } catch (error) {
    console.error('検索エラー:', error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

// SearchConditionAccordionからの再検索
const handleReSearch = async () => {
  // 検索結果の先頭にスクロール
  scrollToResults();

  // searchConditionDataをそのまま使用（コピーしない）
  hasError.value = false;
  isLoading.value = true;

  try {
    const query = buildSearchQuery(searchConditionData.value);
    const response = await searchSimilarTopics({ query });
    results.value = response.results;

    window.sendGAEvent(
      '問診LP_chat',
      're-search',
      `${response.results.length}件`,
    );
  } catch (error) {
    console.error('検索エラー:', error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

// リトライ
const handleRetry = () => {
  hasSearched.value = false;
  hasError.value = false;
  results.value = [];
  currentStepIndex.value = 0;
  lastAnsweredStepIndex.value = -1;
};
</script>

<style lang="scss" scoped>
/* チャット問診エリア */
.chat-questionnaire-section {
  background: #f0fafa;
  padding: 30px 15px 40px;
  min-height: 67vh;
  display: flex;
  flex-direction: column;
}

/* チャットUI */
.chat-questionnaire {
  width: 100%;
  max-width: 345px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.chat-questionnaire__message-list {
  overflow-y: auto;
  max-height: 62vh;
  flex: 1;
}

.chat-questionnaire__message-item {
  margin-bottom: 20px;
  animation: fadeIn 0.3s ease;
}

.chat-questionnaire__input-area {
  margin-top: 20px;
}

.chat-questionnaire__options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 340px;
  margin: 0 auto;
  justify-content: flex-start;
}

.chat-questionnaire__option-btn {
  background: white;
  border: none;
  color: #324646;
  text-align: center;
  padding: 8px 14px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #f0fafa;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    transform: translateY(-0.5px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }
}

.chat-questionnaire__text-input {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  max-width: 300px;
  margin: 0 auto;
}

.chat-questionnaire-free-text {
  :deep(.textarea) {
    width: 100%;
    border: 2px solid #dce1e1;
    border-radius: 5px;
    padding: 12px;
    font-size: 15px;
    line-height: 1.6;
    color: #324646;
    resize: vertical;
    min-height: 100px;
    font-family: inherit;
    box-sizing: border-box;

    &::placeholder {
      color: #b4c3be;
    }

    &:focus {
      outline: none;
      border-color: #0ba780;
    }
  }

  :deep(.counter) {
    text-align: right;
    font-size: 0.875rem;
    color: #7a9490;
    margin-top: 4px;
  }
}

.chat-questionnaire__optional-fields {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dce1e1;
}

.chat-questionnaire__field-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.chat-questionnaire__field-label {
  font-size: 13px;
  color: #324646;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
}

.chat-questionnaire__field-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chat-questionnaire__field-option {
  background: white;
  border: 1px solid #dce1e1;
  color: #324646;
  text-align: center;
  padding: 6px 12px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;

  &:hover {
    background: #f0fafa;
    border-color: #0ba780;
  }

  &.selected {
    background: #0ba780;
    color: white;
    border-color: #0ba780;
  }
}

.chat-questionnaire__text-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.chat-questionnaire__submit-btn {
  background: #0ba780;
  border: none;
  color: white;
  padding: 10px 24px;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #1b755e;
  }

  &:disabled {
    background: #e3e6e6;
    color: #b4c3be;
    cursor: not-allowed;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* レスポンシブ調整 */
@media (max-width: 375px) {
  .chat-questionnaire__bot-bubble,
  .chat-questionnaire__user-bubble {
    max-width: 85%;
  }
}

@media (min-height: 700px) {
  .chat-questionnaire__message-list {
    max-height: 50vh;
  }
}

/* PC向けレスポンシブ */
@media (min-width: 768px) {
  .chat-questionnaire-section {
    padding: 50px 40px 60px;
  }

  .chat-questionnaire {
    max-width: 600px;
    background: white;
    border-radius: 16px;
    padding: 30px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  }

  .chat-questionnaire__message-list {
    max-height: 55vh;
  }

  .chat-questionnaire__intro {
    padding: 30px 20px;
  }

  .chat-questionnaire__intro-icon {
    width: 96px;
    height: 96px;
  }

  .chat-questionnaire__intro-text {
    font-size: 20px;
    line-height: 32px;
  }

  .chat-questionnaire__bot-bubble {
    max-width: 65%;
    padding: 14px 20px;
  }

  .chat-questionnaire__bot-text {
    font-size: 15px;
  }

  .chat-questionnaire__user-bubble {
    max-width: 60%;
    padding: 14px 20px;
  }

  .chat-questionnaire__options {
    max-width: 100%;
    gap: 8px;
  }

  .chat-questionnaire__option-btn {
    padding: 10px 18px;
    font-size: 14px;
    border-radius: 20px;

    &:hover {
      background: #e8f5f0;
      border-color: #0ba780;
    }
  }

  .chat-questionnaire__text-input {
    max-width: 100%;
  }
}

@media (min-width: 1024px) {
  .chat-questionnaire {
    max-width: 680px;
    padding: 40px;
  }

  .chat-questionnaire__bot-bubble {
    max-width: 55%;
  }

  .chat-questionnaire__option-btn {
    padding: 11px 22px;
    font-size: 15px;
  }
}

// 結果表示エリア
.chat-questionnaire__results {
  margin-top: 20px;
  padding: 20px 0;
  background: #fcfaf6;
}
</style>
